/* eslint-disable no-useless-escape */
/**
 * This is the base schema for the MFA component. This schema is defined in JS so that we can easily overwrite / extend
 * the properties for an MFA component instance. This allows us to create the form fields required for MFA via FieldRows and
 * the FormElementFactory.
 *
 * The schema is broken up into rows so that we don't have to supply a separate LayoutSchema / functionality.
 *
 * The properties passed into this come from the address_config_schema in ctm-components/form/address, which are set by
 * passing in the values from the quote_form_schema.
 */

import { get } from 'ctm-util-functions';

import { unitTypes } from './unit_types';

const pageTransitionDisabledRule = {
    rule: 'isTransitioningToNewPage',
    controls: 'disabled'
};

export const getMFASchema = (properties) => {
    return {
        // Sometimes we want to preload the state (eg QLD) from a different area on state, eg form.data.state
        stateOrTerritoryStateKey: properties.stateOrTerritoryStateKey,
        rows: [
            [{
                label: 'Building Name',
                name: 'buildingName',
                field: {
                    type: 'text',
                    sanitizeRegex: new RegExp(/[^a-zA-Z\-\'\s]+/g),
                    maxlength: '255',
                    UIComponent: 'input_auto',
                    optional: true,
                    disableAutocomplete: true
                },
                stateKey: properties.stateKey,
                isDisabled: get(properties, 'renderProps.disabled.buildingName', false),
                identifier: `${properties.backendIdentifier}.buildingName`,
                maskInRecording: true,
                rules: [
                    { ...pageTransitionDisabledRule }
                ]
            },
            {
                label: 'Unit/Shop/Level',
                name: 'unitNumber',
                field: {
                    type: 'text',
                    sanitizeRegex: new RegExp(/[^a-zA-Z0-9\s]+/g),
                    placeholder: 'Unit/Shop/Level',
                    maxlength: '10',
                    UIComponent: 'input_auto',
                    optional: true,
                    disableAutocomplete: true
                },
                stateKey: properties.stateKey,
                isDisabled: get(properties, 'renderProps.disabled.unitNumber', false),
                identifier: `${properties.backendIdentifier}.unitNumber`,
                maskInRecording: true,
                rules: [
                    // @todo fix rules engine so that these rules are not modified with the rule ID
                    { ...pageTransitionDisabledRule }
                ]
            },
            {
                label: 'Unit type',
                name: 'unitType',
                field: {
                    type: 'select',
                    options: unitTypes,
                    optional: true
                },
                stateKey: properties.stateKey,
                isDisabled: get(properties, 'renderProps.disabled.unitType', false),
                identifier: `${properties.backendIdentifier}.unitType`,
                maskInRecording: true,
                rules: [
                    { ...pageTransitionDisabledRule }
                ]
            }],
            [{
                label: 'Street number',
                name: 'streetNumber',
                field: {
                    type: 'text',
                    // eslint-disable-next-line no-useless-escape
                    sanitizeRegex: new RegExp(/[^a-zA-Z0-9\-\/\s]+/g),
                    placeholder: 'St. #',
                    maxlength: '10',
                    UIComponent: 'input_auto',
                    disableAutocomplete: true
                },
                stateKey: properties.stateKey,
                identifier: `${properties.backendIdentifier}.streetNumber`,
                isDisabled: get(properties, 'renderProps.disabled.streetNumber', false),
                maskInRecording: true,
                rules: [
                    { ...pageTransitionDisabledRule },
                    {
                        controls: 'validation',
                        rule: 'isSupplied',
                        props: {
                            stateKey: `${properties.stateKey}.form.${properties.backendIdentifier}.streetNumber`,
                            errorMessages: {
                                isSupplied: 'Please enter a valid street number'
                            }
                        }
                    }
                ]
            },
            {
                label: 'Street',
                name: 'streetName',
                field: {
                    type: 'text',
                    // eslint-disable-next-line no-useless-escape
                    sanitizeRegex: new RegExp(/[^a-zA-Z\-\'\s]+/g),
                    placeholder: 'E.g. Sample Street',
                    maxlength: '255',
                    disableAutocomplete: true
                },
                stateKey: properties.stateKey,
                isDisabled: get(properties, 'renderProps.disabled.streetName', false),
                identifier: `${properties.backendIdentifier}.streetName`,
                maskInRecording: true,
                rules: [
                    { ...pageTransitionDisabledRule },
                    properties.customMFAValidationRules.streetName || {
                        controls: 'validation',
                        rule: 'isSupplied',
                        props: {
                            stateKey: `${properties.stateKey}.form.${properties.backendIdentifier}.streetName`,
                            errorMessages: {
                                isSupplied: 'Please enter a valid street name'
                            }
                        }
                    }
                ]
            }],
            [{
                label: 'Postcode',
                name: 'postcode',
                field: {
                    type: 'formatted',
                    inputType: 'postcode',
                    placeholder: 'Postcode',
                    metaType: 'tel',
                    maxlength: '4',
                    minlength: '4',
                    UIComponent: 'input_auto',
                    disableAutocomplete: true
                },
                stateKey: properties.stateKey,
                // Sometimes we need to preload some fields from a different area on state, eg in energy residentialAddress,
                // we want to preload postcode and suburb from form.data
                preloadFrom: get(properties, 'renderProps.preloadFrom.postcode', null),
                isDisabled: get(properties, 'renderProps.disabled.postcode', false),
                identifier: `${properties.backendIdentifier}.postcode`,
                maskInRecording: true,
                rules: [
                    { ...pageTransitionDisabledRule },
                    {
                        controls: 'validation',
                        // if we have a postcode & suburb from earlier in the journey (eg energy) and we want to ensure that
                        // this address if for the same postcode or suburb, otherwise, just check that something was provided
                        rule: properties.renderProps.matchPostcode ? 'isSupplied && fullAddressMatchesInitialPostcode' : 'isSupplied && validPostCode',
                        props: {
                            stateKey: `${properties.stateKey}.form.${properties.backendIdentifier}.postcode`,
                            errorMessages: {
                                isSupplied: 'Enter postcode',
                                fullAddressMatchesInitialPostcode: 'The entered postcode does not match the original postcode provided.'
                            }
                        }
                    }
                ]
            },
            {
                label: 'Suburb',
                name: 'suburb',
                field: {
                    type: 'select',
                    options: [],
                    UIComponent: 'select_auto',
                    addonWidth: 'auto'
                },
                stateKey: properties.stateKey,
                // Sometimes we need to preload some fields from a different area on state, eg in energy residentialAddress,
                // we want to preload postcode and suburb from form.data
                preloadFrom: get(properties, 'renderProps.preloadFrom.suburb', null),
                isDisabled: get(properties, 'renderProps.disabled.suburb', false),
                identifier: `${properties.backendIdentifier}.suburb`,
                maskInRecording: true,
                rules: [
                    { ...pageTransitionDisabledRule },
                    {
                        controls: 'disabled',
                        rule: '!validPostCode'
                    },
                    {
                        controls: 'validation',
                        rule: 'isSupplied',
                        props: {
                            stateKey: `${properties.stateKey}.form.${properties.backendIdentifier}.suburb`,
                            errorMessages: {
                                isSupplied: 'Please select a suburb'
                            },
                            trigger: 'onBlur'
                        }
                    }
                ]
            }]
        ]
    };
};
